<template>
  <div
    class="relative"
    id="detail-main-bottom-table"
  >
    <div
      v-if="showSelect"
      class="absolute lap-type-select"
      :class="{ 'when-show-statistic': showStatistic }"
    >
      <Select
        v-model="selectedType"
        popup-container="#detail-main-bottom-table"
        :trigger-props="{ position: 'bl', autoFitPopupMinWidth: true }"
      >
        <Select.Option
          v-for="item in lapTypeOptions"
          :key="item.value"
          :data-key="item.value"
          :value="item.value"
        >
          {{ item.label }}
        </Select.Option>
      </Select>
    </div>

    <div
      class="absolute bg-bg-1 table-column-filter-icon right-0"
      v-if="!isGroupSport && !isCustomSport"
    >
      <i
        class="iconfont iconicon_jichutubiao_shaixuan1"
        @click="changeColumnFilterStatus(true)"
      ></i>
    </div>
    <Table
      :scrollbar="false"
      class="activity-detail-table"
      :class="[
        `sport-${summary.sportName}`,
        {
          'when-show-statistic': showStatistic,
          'when-show-select': showSelect,
          'new-lap-style': isNewLapStyle,
          'new-lap-style-bit-3': isNewLapStyle && newListData.length >= 999,
          'new-lap-style-bit-4': isNewLapStyle && newListData.length >= 9999,
          'new-lap-style-bit-5': isNewLapStyle && newListData.length >= 99999
        }
      ]"
      table-layout-fixed
      :loading="tableLoading"
      :columns="showingColumns"
      :bordered="false"
      :data="newListData"
      :pagination="false"
      :row-class="getRowClass"
      @row-click="handleClickRow"
    >
      <template #th="{ column }">
        <BottomTableCustomTh
          :key="column.key"
          :key2="column.key"
          :statisticInfo="column.title && statisticInfo[column.key]"
          :showStatistic="showStatistic"
        />
      </template>
    </Table>

    <!--    列选择弹窗-->
    <ColumnFilter
      ref="columnFilterRef"
      :columnFilterTitle="$t('H6027')"
      :max-column-count="999"
      :original-columns="columnsConfig"
      :columns="tableColumns"
      @save="handleSaveColumnChange"
      @cancel="changeColumnFilterStatus(false)"
    ></ColumnFilter>

    <div :class="['flex-0 cursor-pointer p-10', { hidden: selectRows.length === 0 }]">
      <span
        class="clean-selection"
        @click="cleanSelection"
        >{{ $t('H6025') }}</span
      >
    </div>
  </div>
</template>

<script setup>
import { watch, ref, computed, getCurrentInstance, nextTick } from 'vue';
import { Message, Select, Table } from '@arco-design/web-vue';
import { activityDetailStore } from '@client/store/activityDetailStore';
import ColumnFilter from '@client/components/ColumnFilter/ColumnFilter.vue';
import { useColumnData } from '@client/components/ColumnFilter/useColumnData';
import { columnsDefinition } from './config/columnDefinition';
import Definition from '@client/config/definition';
import { exerciseTypeColor, modeColor } from '@client/pages/activity-detail/config/color';
import BottomTableCustomTh from './BottomTableCustomTh.vue';
import { getSportColumnKeys } from './config/lapTable';
import * as corosSportConfig from '@coros/sport-config';
const { isIndoorClimbOrBoulderingTrainingSportName } = corosSportConfig.sport.climb;

// 表格样式
import './table.scss';
import { currentExpandLapIndex, setExpandLapindex } from './hooks/useExpandLap';
import { lapTypeOptions } from './hooks/useLapTypeOptions';
import { newListData } from './hooks/useLapData';
import { newLapListType, lapListTypeIsLap, setNewLapListType } from './hooks/useLapSelectValue';
import { SportMode } from '@client/types/activity-detail/ApiModel/SportMode';
import { activeTab } from '../../hooks/useActivityTabs';
import { curActivityDetail, useActivityDetail, topSummary } from '../../hooks/useActivityDetail';
import { isNewLapStyle } from './hooks/useIsNewLapStyle';

const vm = getCurrentInstance();
Message._context = vm?.appContext;

const activityDetailStoreRef = activityDetailStore();
const { unitType } = useActivityDetail();
const hasPostureTest = computed(() => activityDetailStoreRef.hasPostureTest);
const tableLoading = computed(() => activityDetailStoreRef.tableLoading);

const isCustomSport = computed(() => Definition.isCustomSport(topSummary.value.sportType));
const sportType = computed(() => curActivityDetail.value?.summary?.sportType);
const summary = computed(() => curActivityDetail.value?.summary);

// 判断是否为(铁人三项、组合运动、登山滑雪)
const isGroupSport = computed(() => activityDetailStoreRef.isGroupSport);

const isAllDistance = computed(() => activityDetailStoreRef.isAllDistance);
const graphObj = computed(() => activityDetailStoreRef.graphObj);
const showSelect = computed(() => {
  // 组合运动的概览不要显示圈选择
  const isGroupShowLogic = activityDetailStoreRef.sportDataDetailList.length === 0 || activeTab.value !== -1;
  return isGroupShowLogic && lapTypeOptions.value.length > 1;
});

// 左侧下拉菜单
const selectedType = ref('');
watch(
  lapTypeOptions,
  () => {
    // console.log(`lapTypeOptions changed`, lapTypeOptions.value);
    const lapTypeOption = lapTypeOptions.value;
    if (lapTypeOption?.[0]?.value) {
      selectedType.value = lapTypeOption[0].value;
    }
  },
  { immediate: true }
);
watch(
  selectedType,
  () => {
    // console.log(`selectedType changed`, selectedType.value);
    // 游泳展开
    setExpandLapindex(undefined);
    setTimeout(() => {
      // 暂时同时设置 newLapListType 和 activityDetailStoreRef.lapListType 以后统一用 newLapListType
      setNewLapListType(selectedType.value);
    }, 0);
    activityDetailStoreRef.changeLapListType(selectedType.value);
  },
  { immediate: true }
);

// 行选择高亮
const selectRows = ref([]);
const selectedData = computed(() => activityDetailStoreRef.selectedData);
watch(selectedData, () => {
  setTimeout(() => {
    if (selectRows.value !== activityDetailStoreRef.selectedData) selectRows.value = activityDetailStoreRef.selectedData;
  });
});
// tr 样式
function getRowClass(record, index) {
  if (record.x_isExpandSub) {
    return [];
  }

  const customizeClass = customizeRowClassName(record);

  return customizeClass.concat([!record.x_isParentData && selectRows.value.includes(record.listRowIndex) ? 'selected-row' : '']);
}

// 恢复
// const isRecoveryType = ({ mode }) => {
//   const sportMode = summary.value.sportMode;
//   const isProgram = !!summary.value.programId;
//   const isOpacity = !isProgram && sportMode === 1 && lapListTypeIsLap.value && !Definition.isExerciseMode(mode);

//   return isOpacity;
// };

// 自定义表格行样式名
function customizeRowClassName(record) {
  // 泳池游泳 公开水域
  if ([300, 301].includes(sportType.value)) {
    // console.log(`preProcessSwimActivityIndex  `, record.uniqueId, record.mode, 'trainindex', record.x_isExpandSub, record.x_isParentData);
    let isOpacity =
      summary.value.sportMode === SportMode.INTERVAL &&
      lapListTypeIsLap.value &&
      // 间歇训练和训练组合：放松和热身、训练一样，都不灰色；只有恢复灰色且没有色块
      Definition.isRecoveryMode(record.mode);
    if (isOpacity || (lapListTypeIsLap.value && record.x_isExpandSub) || (!lapListTypeIsLap.value && record.x_isParentData)) {
      return ['rest-row'];
    }
  } else {
    if (record.isFastest) {
      return ['fastest-row'];
    }

    if (!lapListTypeIsLap.value && record.indexInOriginLap) {
      return [];
    }
    // 新的数据直接判断mode
    const isTrainingRecover = record.mode === 16 && record.exerciseType === 4;
    if (Definition.isRestMode(record.mode) || Definition.isRecoveryMode(record.mode) || isTrainingRecover) {
      return ['rest-row'];
    }
  }
  return [];
}
// 点击行之前（返回true则允许选择，返回false则终止）
function beforeRowClick(rowData) {
  const { sportName } = summary.value;
  if (rowData.x_isExpandSub || (['Strength'].includes(sportName) && Definition.isRestMode(rowData.mode))) {
    return false;
  }
  return true;
}
// tr 点击
function handleClickRow(record, ev) {
  if (record.x_isExpandSub || record.x_isParentData || !beforeRowClick(record)) return;

  const index = record.listRowIndex;
  let newData = JSON.parse(JSON.stringify(selectRows.value));
  const idx = newData.indexOf(index);
  if (idx > -1) newData.splice(idx, 1);
  else newData.push(index);
  const { sportName } = summary.value;
  // 室内攀岩、抱石圈速表里面过滤了恢复圈(mode=3 且 showRestMode=0)，但是图表要显示 mode=3 的内容。所以选择 mode=1 的时候要自动加上 mode=3 的内容
  if (isIndoorClimbOrBoulderingTrainingSportName(sportName)) {
    newListData.value
      // mode=3 showRestMode=1 的已经显示了，不用处理
      .filter((i) => i.mode === 3 && i.showRestMode === 0)
      .forEach((row) => {
        if (row.mode === 3) {
          // 查找对应的 mode=1 的内容
          const findMode1RowIdx = newData.indexOf(row.listRowIndex - 1);
          const findMode3RowIdx = newData.indexOf(row.listRowIndex);
          if (findMode1RowIdx > -1 && findMode3RowIdx === -1) {
            // console.log(`add mode=3 listrowindex`);
            newData.push(row.listRowIndex);
          } else if (findMode1RowIdx === -1 && findMode3RowIdx > -1) {
            // console.log(`delete listrowindex`, newData, findMode3RowIdx);
            newData.splice(findMode3RowIdx, 1);
          }
        }
      });
  }

  setTimeout(() => activityDetailStoreRef.lapSelectionChange(newData), 0);
}
function cleanSelection() {
  setTimeout(() => activityDetailStoreRef.lapSelectionChange([]), 0);
}
// 根据当前选中的运动分类筛选出对应的列定义
const columnsConfig = computed(() => {
  // 多段攀登 lapTypeOptions.value 为空
  if (lapTypeOptions.value.length > 1 && !newLapListType.value) {
    return [];
  }
  const { sportName } = summary.value;

  const _columnsDefinition = columnsDefinition({
    isGroupSportRef: isGroupSport,
    listData: newListData,
    summary: summary.value,
    unitType: unitType.value,
    sportType: sportType,
    sportName,
    graphObjRef: graphObj,
    isAllDistanceRef: isAllDistance,
    getColorRectBg,
    hasPostureTest,
    // 展开圈数表某一项
    setExpandLapindex,
    // 当前展开的圈数表的索引
    currentExpandLapIndex,
    isNewLapStyle
  });

  const sportColumnKeys = getSportColumnKeys(topSummary, summary);
  if (!sportColumnKeys) return [];

  const result = sportColumnKeys
    .concat([
      {
        key: 'Right_Gap',
        config: { status: true }
      }
    ])
    .map((item) => {
      const key = item.key;
      const column = _columnsDefinition[key] || {};
      const { width, minWidth, ...others } = column;
      const newItem = {
        ...others,
        // width: column.width ?? 100,
        ...(item.config || {}),
        key
      };
      if (key === 'Left_Gap' && !showSelect.value) {
        // newItem.width = 20
        newItem.style = {
          'min-width': '50px'
        };
      } else if (minWidth) {
        newItem.width = minWidth;
        newItem['min-width'] = minWidth;
      }

      return newItem;
    })
    .filter((item, index) => {
      // return true
      return item.useIt ? item.useIt() : true;
    });
  return result;
});

const { userSettings, tableColumns, showingColumns, handleSaveColumnChange, changeActiveColumns } = useColumnData({
  columnsConfig,
  dataKey: 'activityDetailLapList',
  sportType
});
const columnFilterRef = ref(null);
function changeColumnFilterStatus(status) {
  if (status) columnFilterRef.value.open();
}

// 根据用户保存的列配置初始化表格列的状态
watch(userSettings, () => {
  changeActiveColumns(columnsConfig.value);
});
// 废弃使用 sportType 监听，因为可能多个 sportType 相同
// watch(sportType, () => {
//   console.log(`bottomTable sportType changed`, sportType.value);
//   changeActiveColumns(columnsConfig.value);
// });
watch(activeTab, () => {
  // 延迟执行，因为 activeTab 改变后要等待 lapTypeOption 和 newLapListType 改变
  nextTick(() => {
    setTimeout(() => {
      console.log(`bottomTable activeTab changed`, activeTab.value);
      changeActiveColumns(columnsConfig.value);
    }, 0);
  });
});

const frameSelectedData = computed(() => activityDetailStoreRef.frameSelectedData);
const frameSelection = computed(() => activityDetailStoreRef.frameSelection);
const gettingFrequency = computed(() => activityDetailStoreRef.gettingFrequency);

// 是否显示汇总行
const showStatistic = computed(() => {
  if (frameSelectedData.value && frameSelection.value.data.length > 0 && !gettingFrequency.value) {
    return true;
  }
  if (!isGroupSport.value && selectedData.value.length >= 2) {
    return true;
  }
  return false;
});

// 汇总行数据
const customStatisticInfo = computed(() => {
  const { data: dataInFrameSelection, lapInfo } = frameSelection.value;
  if (!dataInFrameSelection || !frameSelectedData.value || dataInFrameSelection.length === 0 || gettingFrequency.value) {
    return null;
  }

  const result = {};
  showingColumns.value.forEach((item) => {
    if (typeof item.frequencyListStatistic === 'function') {
      result[item.key] = item.frequencyListStatistic({ dataInFrameSelection, lapInfo });
    } else {
      result[item.key] = '--';
    }
  });
  return result;
});

const statisticInfo = computed(() => {
  if (customStatisticInfo.value) {
    return customStatisticInfo.value;
  }

  const result = {};
  if (!selectedData.value.length) {
    return result;
  }

  const selectItems = selectedData.value
    .map((listRowIndex) => {
      return newListData.value.find((i) => i.listRowIndex === listRowIndex);
    })
    // 恢复圈过滤后，这里会出现 undefined
    .filter(Boolean);
  showingColumns.value.forEach((item) => {
    if (typeof item.statistic === 'function') {
      result[item.key] = item.statistic(selectItems);
    }
  });

  return result;
});

function isNextRowRestRow(row, nextRow) {
  const flag = nextRow && nextRow?.exerciseIndex === row.exerciseIndex && nextRow.setIndex === row.setIndex;
  return flag;
}

function getColorRectBg(row, index) {
  if (row.x_isExpandSub) {
    return 'transparent';
  }

  // 游泳插入了展开行，不能走这个逻辑
  if (sportType.value !== 300) {
    // mode不是休息不一定不是休息，旧数据的休息根据exerciseIndex、setIndex判断
    const lastData2 = newListData.value[index - 1];
    if (isNextRowRestRow(row, lastData2)) {
      return 'transparent';
    }
  }

  if (row.exerciseType) {
    return exerciseTypeColor[row.exerciseType];
  }

  return modeColor[row.mode];
}
</script>

<style lang="scss">
@import '@client/styles/_mixins.scss';

.title-statistic {
  padding: 4px;
  box-sizing: border-box;

  &.has-border {
    border-top: 1px solid hsla(0, 0%, 100%, 0.1);
    margin-top: 8px;
  }

  .value {
    font-size: 14px;
    color: rgba(255, 255, 255, 1);
  }
  .unit {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
  }
}
.activity-name {
  word-break: break-word;
}
</style>

<style lang="scss" scoped>
@import '@client/styles/_mixins.scss';
.table-column-filter-icon {
  min-height: 36px;
  padding: 0 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  z-index: 120;
  .iconfont {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.6);
  }
}
/**
 1. 去掉 background
 2. 自定义的选中样式
 3.
 */
.lap-type-select {
  top: 0;
  background-color: rgb(13, 19, 38);
  padding: 0;
  max-width: px2rem144(100);
  z-index: 120;

  // &.when-show-statistic {
  // }
}

.activity-detail-table {
  :deep(.arco-table-footer) {
    width: 100%;
    padding: 0;
    border: none;
  }

  // th 宽高
  &.when-show-statistic :deep(thead th.arco-table-th) {
    > span {
      padding: 0px 4px !important;
    }
  }

  :deep(td .index) {
    font-size: 16px;
  }
  :deep(td .trasition) {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.4);
  }
  :deep(.fastest-tips) {
    padding: 0 8px;
    font-size: 14px;
    color: #00b3ff;
    white-space: nowrap;
  }
  :deep(.color-rect) {
    position: absolute;
    top: 50%;
    left: 0;
    transform: translate(0, -50%);
    width: 8px;
    height: 40px;
  }

  //休息行
  :deep(.rest-row td) {
    color: rgba(255, 255, 255, 0.5) !important;
  }

  &.sport-Strength,
  &.new-lap-style {
    :deep(.arco-table-td-content) {
      display: flex;
    }

    // 序号右对齐、文字左对齐，表头对齐文字
    :deep(.arco-table-th.field-Jump_Rope_Lengths .arco-table-th-title),
    :deep(.arco-table-th.field-Laps_Lengths .arco-table-th-title),
    :deep(.arco-table-th.field-Action_Name .arco-table-th-title) {
      margin-left: 31px;
    }
    // 游泳序号样式特殊
    :deep(.activity-index) {
      width: 27px;
      text-align: right;
      margin-right: 4px;
      display: inline-block;
      flex-shrink: 0;
    }

    &.new-lap-style-bit-3 {
      :deep(.arco-table-th.field-Jump_Rope_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Laps_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Action_Name .arco-table-th-title) {
        margin-left: 36px;
      }
      :deep(.activity-index) {
        width: 31px;
      }
    }
    &.new-lap-style-bit-4 {
      :deep(.arco-table-th.field-Jump_Rope_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Laps_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Action_Name .arco-table-th-title) {
        margin-left: 41px;
      }
      :deep(.activity-index) {
        width: 36px;
      }
    }
    &.new-lap-style-bit-5 {
      :deep(.arco-table-th.field-Jump_Rope_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Laps_Lengths .arco-table-th-title),
      :deep(.arco-table-th.field-Action_Name .arco-table-th-title) {
        margin-left: 49px;
      }
      :deep(.activity-index) {
        width: 44px;
      }
    }
  }
}

.clean-selection {
  color: #00affa;
  cursor: pointer;
  font-size: 16px;
}
</style>
